/**
 * Pad reveal scroll math (Plan 12-13, 12-UAT-G1 / G2).
 *
 * SignDialog's body flows as one column inside MidFlowModal's body element,
 * which is the only scroller. On a short viewport (phone landscape, or a
 * small portrait phone with a long summary) the signature pad can sit below
 * that scroller's visible box. This pure function picks the scrollTop that
 * brings the pad into view, so the component only measures and assigns.
 *
 * Tiers are vertical spans in client px, largest first:
 *   1. the whole pad (label row with Undo/Clear, the well, the hint),
 *   2. the label row + the well,
 *   3. the well alone (the drawing surface).
 * The first tier that fits the view wins, so the customer sees as much of
 * the pad as the viewport allows and always the whole canvas when it fits.
 * When nothing fits, the last tier's top is aligned with the view top.
 *
 * No DOM access: callers pass measured rects.
 */

/** A vertical span in client (viewport) px. */
export interface VerticalSpan {
  top: number;
  bottom: number;
}

export interface PadRevealInput {
  /** The scroller's visible client box (rect top + clientTop, + clientHeight). */
  view: VerticalSpan;
  /** Pad spans, largest first (whole pad, label row + well, well). */
  tiers: ReadonlyArray<VerticalSpan>;
  /** The scroller's current scrollTop. */
  scrollTop: number;
  /** scrollHeight - clientHeight of the scroller. */
  maxScrollTop: number;
}

/** Sub-pixel layout noise allowed when deciding a tier is already in view. */
const INSIDE_TOLERANCE = 0.5;

function isFiniteSpan(span: VerticalSpan): boolean {
  return Number.isFinite(span.top) && Number.isFinite(span.bottom);
}

function height(span: VerticalSpan): number {
  return span.bottom - span.top;
}

/** The scroll delta that brings the tier into the view by its nearest edge. */
function deltaFor(tier: VerticalSpan, view: VerticalSpan): number | null {
  const inside =
    tier.top >= view.top - INSIDE_TOLERANCE &&
    tier.bottom <= view.bottom + INSIDE_TOLERANCE;
  if (inside) return null;
  return tier.top < view.top ? tier.top - view.top : tier.bottom - view.bottom;
}

/**
 * Returns the scrollTop that reveals the pad inside its scroller, or `null`
 * when no scroll is needed (already visible, a change under 1px) or the
 * input is unusable (a non-finite number, a non-positive view height, no
 * tiers).
 */
export function padRevealScrollTop(input: PadRevealInput): number | null {
  const { view, tiers, scrollTop, maxScrollTop } = input;
  if (
    !isFiniteSpan(view) ||
    !Number.isFinite(scrollTop) ||
    !Number.isFinite(maxScrollTop) ||
    !tiers.every(isFiniteSpan)
  ) {
    return null;
  }
  const viewHeight = height(view);
  if (viewHeight <= 0 || tiers.length === 0) return null;

  const fitting = tiers.find((tier) => height(tier) <= viewHeight);
  let delta: number;
  if (fitting) {
    const move = deltaFor(fitting, view);
    if (move === null) return null;
    delta = move;
  } else {
    delta = tiers[tiers.length - 1].top - view.top;
  }

  const next = Math.min(
    Math.max(Math.round(scrollTop + delta), 0),
    Math.max(maxScrollTop, 0),
  );
  return Math.abs(next - scrollTop) < 1 ? null : next;
}
